// @flow

import React, { Fragment } from 'react';

import { Link } from '@zapier/design-system';

import help from 'app/get-help/components/blocks/Help';
import radio from 'app/get-help/components/blocks/Radio';
import {
  createMediaField,
  getDefaultFields,
  getUserFields,
  messageField,
} from 'app/get-help/content/nodes/common';
import { ZENDESK_CUSTOM_FIELD_IDS } from 'app/get-help/constants';


const createZapChild = (isLoggedIn: boolean) => {
  const sharedUserFields = [
    ...getUserFields(isLoggedIn),
    {
      ...messageField,
      label: `Please tell us a little more so we can help you quicker.`,
      details: `Please note the apps you are using and what you are hoping to accomplish. If you have an example that illustrates your question, please provide that as well.`,
    },
    createMediaField(),
  ];

  return radio({
    id: 'problem',
    label: 'What can we help you with?',
    zendeskCustomFieldId: ZENDESK_CUSTOM_FIELD_IDS.ZAPS_WHAT_CAN_WE_HELP_WITH,
    items: [
      {
        value: 'integrate',
        label: 'Building workflows with my apps',
        zendeskCustomFieldValue: 'cf-building-workflows',
        child: help({
          docs: {
            title: 'Browse our apps and built-in tools',
            body: () => (
              <Fragment>
                <p>
                  To check if your workflow idea might be possible, try visiting
                  our <Link href="/apps">App Directory</Link> and select the
                  first app. On the next page, search for the second app you'd
                  like to connect it with. We'll show you popular ways to
                  connect them, as well as a list of every custom combination
                  between them you can set up to get you automating your
                  workflow right away.
                </p>
                <p>
                  There are a few handy Zapier tools that can help create your
                  perfect workflow! Use the{' '}
                  <Link href="/apps/filter">Filter step</Link> so that your Zap
                  only runs if the item fulfills certain criteria you set. Or
                  use <Link href="/apps/formatter">Formatter</Link> to change
                  the format of your data, and{' '}
                  <Link href="/apps/email-parser">Email Parser</Link> to parse
                  your emails into information that can be sent to other apps.{' '}
                  <Link href="/apps/categories/zapier-tools">
                    Check out our list of all Zapier built-in tools
                  </Link>{' '}
                  so you can customize the perfect workflow for you.
                </p>
              </Fragment>
            ),
          },
          community: [
            {
              title:
                'Parsing or Saving CSV Email Attachment into Google Sheets',
              href:
                'https://community.zapier.com/discussion/530/parsing-or-saving-csv-email-attachment-into-google-sheets#latest',
              snippet:
                'Using Formatter by Zapier, a Zap creator can add CSVs to Google Sheets.',
            },
            {
              title:
                'How to automatically send channel messages from Trello to slack via Zapier',
              href:
                'https://community.zapier.com/discussion/497/how-to-automatically-send-channel-messages-from-trello-to-slack-via-zapier#latest',
              snippet:
                'Using Trello’s Card Due trigger, a Community member can create a Zap to send a message to a Slack channel on a card’s due date.',
            },
            {
              title: 'Dynamic account lookup with Zapier',
              href:
                'https://community.zapier.com/discussion/473/dynamic-account-lookup-with-zapier#latest',
              snippet:
                'A Zapier Expert explains how to pass data from one Zap to another using Google Sheets.',
            },
          ],
          contact: sharedUserFields,
        }),
      },
      {
        value: 'filtering',
        label: 'Filtering or triggering only on certain items',
        zendeskCustomFieldValue: 'cf-filtering',
        child: help({
          docs: {
            title: 'Add a filter to your Zap',
            body: () => (
              <p>
                To filter your Zap so that it only runs on certain items that
                fit your criteria,{' '}
                <Link href="/help/create/customize/add-conditions-to-zaps-with-filters">
                  use a Filter step
                </Link>
                . This allows you to define criteria, so that the Zap will only
                continue with later steps if there's a match.
              </p>
            ),
          },
          // TODO: Enable once we have enough resources for community moderation
          // community: [
          //   {
          //     title: 'Filter by Zapier by Comparing Two Variables',
          //     href:
          //       'https://community.zapier.com/discussion/362/filter-by-zapier-by-comparing-two-variables#latest',
          //     snippet:
          //       'A moderator explains how to use variables in filters, and gives tips to ensure it’ll work.',
          //   },
          //   {
          //     title:
          //       '"My Zap keeps triggering on the same information!" - how can you create your own deduplication?',
          //     href:
          //       'https://community.zapier.com/discussion/221/my-zap-keeps-triggering-on-the-same-information-how-can-you-create-your-own-deduplication',
          //     snippet:
          //       'A Zapier Expert explains how to use filters to prevent the same data from triggering a Zap more than once.',
          //   },
          //   {
          //     title: 'How to use dynamic fields in filters',
          //     href:
          //       'https://community.zapier.com/discussion/211/how-to-use-dynamic-fields-in-filters',
          //     snippet:
          //       'A Zapier Expert shares a workaround that allows users to make filter conditions update dynamically.',
          //   },
          // ],
          contact: sharedUserFields,
        }),
      },
      {
        value: 'paths',
        label: 'Using paths or creating different and branching outcomes',
        zendeskCustomFieldValue: 'cf-paths-or-branches',
        child: help({
          docs: {
            title: 'Add a path to your Zap or add filters to multiple Zaps',
            body: () => (
              <Fragment>
                <p>
                  There are two ways of adding paths or branching logic to your
                  Zap, depending on your pricing plan.
                </p>
                <ol>
                  <li>
                    If you're on the Free or Starter plan, you can use Filter
                    steps and multiple Zaps to achieve multiple outcomes by{' '}
                    <Link href="/help/create/customize/add-branching-logic-to-zaps-with-filters">
                      following this tutorial
                    </Link>
                    .
                  </li>
                  <li>
                    If you're on a Professional, Professional Plus, or Team
                    plan, you can use Paths in your Zap to define different
                    outcomes. For help on setting up Paths as well as answers to
                    common questions,{' '}
                    <Link href="/help/create/customize/add-branching-logic-to-zaps-with-paths">
                      check out this guide
                    </Link>
                    .
                  </li>
                </ol>
              </Fragment>
            ),
          },
          // TODO: Enable once we have enough resources for community moderation
          // community: [
          //   {
          //     title: 'Prevent a path from running more than once per day?',
          //     href:
          //       'https://community.zapier.com/discussion/382/prevent-a-path-from-running-more-that-once-per-day#latest',
          //     snippet:
          //       'A Zapier Expert explains how to use the filters and storage steps to only perform an action once per day, regardless of how often a Zap is triggered.',
          //   },
          //   {
          //     title: 'Is there a way to merge paths back together?',
          //     href:
          //       'https://community.zapier.com/discussion/112/is-there-a-way-to-merge-paths-back-together',
          //     snippet:
          //       'A Zapier Expert explains how to use webhooks to merge paths back together.',
          //   },
          //   {
          //     title:
          //       'Building a workflow with multiple "if" conditions that can run concurrently',
          //     href:
          //       'https://community.zapier.com/discussion/77/building-a-workflow-with-multiple-if-conditions-that-can-run-concurrently',
          //     snippet:
          //       'A Zapier Expert shares the code step they used to ensure multiple paths run at the same time.',
          //   },
          // ],
          contact: sharedUserFields,
        }),
      },
      {
        value: 'datetime',
        label: 'Working with dates, times, and other timing issues',
        zendeskCustomFieldValue: 'cf-dates-timing-issues',
        child: help({
          docs: {
            title: 'Add a schedule trigger or delay or format data',
            body: () => (
              <Fragment>
                <p>
                  There are several Zapier tools that can help you adjust dates
                  and times on Zapier.
                </p>
                <ol>
                  <li>
                    <Link href="/help/create/customize/schedule-zaps-to-run-at-specific-intervals">
                      Use Schedule
                    </Link>{' '}
                    as a trigger to schedule your Zap to start running at
                    specific times or intervals.
                  </li>
                  <li>
                    <Link href="/help/create/format">Use Formatter steps</Link>{' '}
                    to change date formats, add and subtract time, and much
                    more.
                  </li>
                  <li>
                    <Link href="/help/create/customize/add-delays-to-zaps">
                      Use Delay steps
                    </Link>{' '}
                    to delay your actions, so they'll run at a later time.
                  </li>
                </ol>
              </Fragment>
            ),
          },
          // TODO: Enable once we have enough resources for community moderation
          // community: [
          //   {
          //     title: 'Dates to trigger slack message',
          //     href:
          //       'https://community.zapier.com/discussion/569/dates-to-trigger-slack-message#latest',
          //     snippet:
          //       'A Zap creator wants a Slack notification before a date that’s different every time the Zap triggers. A Zapier Expert recommends using Formatter and Delay.',
          //   },
          //   {
          //     title: 'Delay Until error',
          //     href:
          //       'https://community.zapier.com/discussion/443/delay-until-error#latest',
          //     snippet:
          //       'An action step is looking for a date formatted MM/DD/YYYY, but the trigger is sending a date formatted DD/MM/YYY.',
          //   },
          //   {
          //     title: 'Can I add business days to a formatter calculation?',
          //     href:
          //       'https://community.zapier.com/discussion/379/can-i-add-business-days-to-a-formatter-calculation#latest',
          //     snippet:
          //       'A Zapier Expert recommends using Google Sheets to calculate business days and send the value to Zapier.',
          //   },
          // ],
          contact: sharedUserFields,
        }),
      },
      {
        value: 'transfer',
        label: 'Transferring Zaps between accounts',
        zendeskCustomFieldValue: 'cf-transferring-zaps',
        child: help({
          docs: {
            title: 'Transfer Zaps between folders or recreate Zaps',
            body: () =>
              isLoggedIn ? (
                <Fragment>
                  <p>
                    If you're on a Zapier for Teams account, you can transfer
                    Zaps both from the team folder into your private folder and
                    from your private folder into your team folder by{' '}
                    <Link href="/help/manage/collaborate/transfer-zaps-from-your-personal-to-team-account">
                      following these instructions
                    </Link>
                    . You can also transfer Zaps to and from another teammate
                    using this method as long as you are both part of the same
                    team.
                  </p>
                  <p>
                    If you are not on a Team account, unfortunately there's not
                    a direct way to transfer Zaps between accounts. You either
                    rebuild the Zaps on the new account,{' '}
                    <Link href="/app/settings/manage-email">
                      change the email on the account in your settings
                    </Link>{' '}
                    to the new owner's, or temporarily upgrade to a Team account
                    to follow the instructions above and then downgrade once
                    that's completed (any remaining payment will remain as a
                    credit on your account).
                  </p>
                </Fragment>
              ) : (
                <Fragment>
                  <p>
                    Right now, the only way to transfer Zaps between accounts is
                    through a Zapier for Teams account. Members of the same team
                    can easily transfer Zaps to each other by{' '}
                    <Link href="/help/manage/collaborate/transfer-zaps-from-your-personal-to-team-account">
                      following these instructions
                    </Link>
                    , making sharing workflows easy.
                  </p>
                  <p>
                    Unfortunately there's not a direct way to transfer Zaps
                    between accounts if you don't have a Team account. Zaps will
                    have to be rebuilt, or you can temporarily upgrade to Team
                    to follow the instructions above.
                  </p>
                </Fragment>
              ),
          },
          // TODO: Enable once we have enough resources for community moderation
          // community: [
          //   {
          //     title: 'Introducing Super Simple Shared Zap Updates!',
          //     href:
          //       'https://community.zapier.com/discussion/570/introducing-super-simple-shared-zap-updates',
          //     snippet:
          //       'An engineer at Zapier explains how to use Zapier’s sharing feature to send your Zaps to friends, family and co-workers.',
          //   },
          // ],
          contact: sharedUserFields,
        }),
      },
      {
        value: 'expert',
        label: `Hiring an expert to build Zaps`,
        zendeskCustomFieldValue: 'cf-hiring-experts',
        child: help({
          docs: {
            title: 'Browse the directory of Zapier Certified Experts',
            body: () => (
              <p>
                Browse our{' '}
                <Link href="/experts/">
                  directory of Certified Zapier Experts
                </Link>{' '}
                or post your job within the{' '}
                <Link href="https://community.zapier.com/hire-a-pro-12">
                  Hire a Pro
                </Link>{' '}
                category of our Community to hire someone who can help you craft
                the perfect workflow.
              </p>
            ),
          },
          contact: getDefaultFields(isLoggedIn),
        }),
      },
      {
        value: 'other',
        label: 'Other issues creating or managing Zaps',
        zendeskCustomFieldValue: 'cf-other-managing',
        child: help({
          community: [
            {
              title: 'Daily Limit Exceeded',
              href:
                'https://community.zapier.com/discussion/comment/1849#Comment_1849',
              snippet:
                'Some apps have limits on the amount of data passed to other apps. This conversation explains some ways to avoid hitting those limits.',
            },
            {
              title:
                'Building a workflow with multiple "if" conditions that can run concurrently',
              href:
                'https://community.zapier.com/discussion/77/building-a-workflow-with-multiple-if-conditions-that-can-run-concurrently',
              snippet:
                'A Zapier Expert shares the code step they used to ensure multiple paths run at the same time.',
            },
            {
              title:
                'How can I create a Zap/workflow that only runs during business hours?',
              href:
                'https://community.zapier.com/discussion/72/how-can-i-create-a-zap-workflow-that-only-runs-during-business-hours',
              snippet:
                'A Zapier Expert shares how they used a code step to make sure a Zap would only run during business hours.',
            },
          ],
          contact: sharedUserFields,
        }),
      },
    ],
  });
};

export default createZapChild;
